import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import store from "../store/store";
import { useAppDispatch, useAppSelector } from "../store/store";
import { signIn, initializeAuth } from "../store/userSlice";
import AppInitWrapper from "./AppInitWrapper";
import PWAWrapper from "./PWAWrapper";
import Text from "@amzn/meridian/text";
import Column from "@amzn/meridian/column";
import AuthorizedContent from "./AuthorizedContent";
import Loader from "@amzn/meridian/loader";
import AppToaster from "./UI/appToaster";
import DeviceCallbackPage from "./pages/deviceFlow/DeviceCallbackPage";
import { deviceDetectionService } from "../services/deviceDetectionService";
import { cloudWatchLogger } from "../services/CloudWatchLoggerService";
import { fetchFeatureFlags } from "src/store/featureFlagsSlice";
import { checkForWebsiteUpdates } from "src/utils/checkForWebsiteUpdates";

const UPDATE_CHECK_FREQUENCY = 3_600_000; // 1 hour expressed as milliseconds

const AppContent = () => {
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.user);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  return (
    <Routes>
      {/* Device flow callback route - must be handled before authentication check */}
      <Route path="/device_callback" element={<DeviceCallbackPage />} />
      
      {/* Main application routes */}
      <Route path="/*" element={<MainApp />} />
    </Routes>
  );
};

const MainApp = () => {
  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.user);

  useEffect(() => {
    if (userState.userLoading === "fulfilled" && userState.alias) {
      cloudWatchLogger.setUserId(userState.alias);
    }
  }, [userState.userLoading, userState.alias]);

  useEffect(() => {
    if (userState.userLoading === "fulfilled" && userState.alias) {
        dispatch(fetchFeatureFlags());
    }
  }, [dispatch, userState.userLoading, userState.alias]);

  useEffect(() => {
    const intervalId = setInterval(() => {
        checkForWebsiteUpdates()
    }, UPDATE_CHECK_FREQUENCY);

    return () => clearInterval(intervalId);
  }, []);

  // Show loading state while authenticating
  if (userState.isLoading) {
    return (
      <Column
        width={"100vw"}
        height={"100vh"}
        alignmentHorizontal="center"
        alignmentVertical="center"
      >
        <Loader size="large" type="circular" />
        <Text>Authenticating user...</Text>
      </Column>
    );
  } else if (
    userState.userLoading === "fulfilled" &&
    !userState.hasPermission
  ) {
    return (
      <div>
        <h2>Access Denied</h2>
        <p>
          You don't have the required permissions to access this application.
        </p>
        <p>
          Required groups: whs-data-eap-user, whs-data-eap-admin, or
          workingwell-engineer
        </p>
        <p>
          User: {userState.email} | Groups:{" "}
          {userState.ldap.join(", ") || "None"}
        </p>
        <button onClick={() => dispatch(signIn())}>Try Sign In Again</button>
      </div>
    );
  } else if (userState.userLoading === "fulfilled" && userState.hasPermission) {
    return (
      <>
        <AppToaster />
        <AuthorizedContent />
      </>
    );
  }

  // Show sign-in prompt if not authenticated
  const deviceType = deviceDetectionService.getDeviceType();
  
  return (
    <div>
      <h2>Safety Genie Application</h2>
      <div>Please sign in to access the application</div>
      <button
        onClick={async () => {
          try {
            await dispatch(signIn()).unwrap();
          } catch (error) {
            console.error("Sign-in failed:", error);
          }
        }}
      >
        Sign In
      </button>
    </div>
  );
};

const AppWithProvider = () => {
  return (
    <BrowserRouter>
      <Provider store={store}>
        <PWAWrapper>
          <AppInitWrapper>
            <AppContent />
          </AppInitWrapper>
        </PWAWrapper>
      </Provider>
    </BrowserRouter>
  );
};

export default AppWithProvider;
